<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
	HUB_TEXT_SIZE_OPTIONS,
	SCALE_BY_SIZE,
	applyHubTextSize,
	deleteOwnAvatar,
	fetchAccountAvatarProfile,
	getAppliedHubTextSize,
	isClientUser,
	setHubTextSize,
	uploadOwnAvatar,
	type HubTextSize,
} from '@apexx/auth';
import { Avatar, Typography } from '@apexx/component-library';

import { performHubLogout } from '@/auth/logout';
import { useHubSettingsDrawer } from '@/composables/useHubSettingsDrawer';
import { useHubAuthStore } from '@/store/hubAuthStore';

import { createHubTextSizeDraftSession } from './hubTextSizeDraft';

const router = useRouter();
const authStore = useHubAuthStore();
const { closeSettingsDrawer } = useHubSettingsDrawer();

const draftSession = createHubTextSizeDraftSession(getAppliedHubTextSize(), applyHubTextSize);
/** Draft selection (preview only until Save). */
const textSize = draftSession.draft;
const syncError = ref<string | null>(null);
const isSaving = ref(false);
const isLoggingOut = ref(false);

/** Clients cannot manage profile photos in hub settings. */
const showAvatarSettings = computed(() => !isClientUser(authStore.accessToken));

const pictureUrl = ref<string | null>(null);
const initials = ref('?');
const avatarError = ref<string | null>(null);
const isAvatarBusy = ref(false);
const isAvatarLoading = ref(true);
const fileInputRef = ref<HTMLInputElement | null>(null);

const options = HUB_TEXT_SIZE_OPTIONS;
const isDirty = draftSession.isDirty;

/**
 * Fixed preview size for each option.
 * Uses px against a 16px medium baseline so labels/Aa do not track html rem scale
 * (CSS zoom is unreliable in Android WebView).
 */
function optionBodyFontSize(size: HubTextSize): string {
	return `${16 * SCALE_BY_SIZE[size]}px`;
}

function selectSize(size: HubTextSize): void {
	if (isSaving.value || textSize.value === size) {
		return;
	}

	draftSession.select(size);
	syncError.value = null;
}

async function save(): Promise<void> {
	if (!isDirty.value || isSaving.value) {
		return;
	}

	syncError.value = null;
	isSaving.value = true;

	try {
		const result = await setHubTextSize(textSize.value, authStore.accessToken);
		draftSession.markSaved();
		if (!result.synced && result.error) {
			syncError.value = result.error;
		}
	} finally {
		isSaving.value = false;
	}
}

function cancel(): void {
	draftSession.revert();
	closeSettingsDrawer();
}

async function loadAvatarProfile(): Promise<void> {
	if (!showAvatarSettings.value) {
		isAvatarLoading.value = false;
		return;
	}

	const token = authStore.accessToken;
	if (!token) {
		isAvatarLoading.value = false;
		return;
	}

	isAvatarLoading.value = true;
	avatarError.value = null;

	try {
		const profile = await fetchAccountAvatarProfile(token);
		pictureUrl.value = profile.picture;
		initials.value = profile.initials;
	} catch (error) {
		avatarError.value =
			error instanceof Error
				? error.message
				: 'Failed to load profile photo';
	} finally {
		isAvatarLoading.value = false;
	}
}

function openFilePicker(): void {
	if (isAvatarBusy.value) {
		return;
	}
	fileInputRef.value?.click();
}

async function onFileSelected(event: Event): Promise<void> {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	input.value = '';

	if (!file || !authStore.accessToken) {
		return;
	}

	avatarError.value = null;
	isAvatarBusy.value = true;

	try {
		pictureUrl.value = await uploadOwnAvatar(authStore.accessToken, file);
	} catch (error) {
		avatarError.value =
			error instanceof Error ? error.message : 'Failed to upload photo';
	} finally {
		isAvatarBusy.value = false;
	}
}

async function removeAvatar(): Promise<void> {
	if (!authStore.accessToken || !pictureUrl.value || isAvatarBusy.value) {
		return;
	}

	avatarError.value = null;
	isAvatarBusy.value = true;

	try {
		await deleteOwnAvatar(authStore.accessToken);
		pictureUrl.value = null;
	} catch (error) {
		avatarError.value =
			error instanceof Error ? error.message : 'Failed to remove photo';
	} finally {
		isAvatarBusy.value = false;
	}
}

async function logout(): Promise<void> {
	if (isLoggingOut.value) {
		return;
	}

	isLoggingOut.value = true;
	draftSession.revert();
	closeSettingsDrawer();
	try {
		await performHubLogout(router, { reason: 'user-sign-out' });
	} finally {
		isLoggingOut.value = false;
	}
}

onMounted(() => {
	void loadAvatarProfile();
});

onBeforeUnmount(() => {
	draftSession.revert();
});

defineExpose({
	save,
	cancel,
	isDirty,
	isSaving,
});
</script>

<template>
	<div class="flex w-full flex-col gap-8 pb-2">
		<section
			v-if="showAvatarSettings"
			class="flex flex-col items-center gap-3"
		>
			<Typography
				variant="title"
				as="h2"
				class="self-start font-semibold text-on-surface"
			>
				Profile photo
			</Typography>

			<div class="relative">
				<Avatar
					:src="pictureUrl ?? undefined"
					:initials="initials"
					:alt="'Profile photo'"
					size="lg"
				/>
				<span
					v-if="isAvatarLoading || isAvatarBusy"
					class="absolute inset-0 flex items-center justify-center rounded-full bg-surface/70 text-caption text-on-surface-muted"
					aria-live="polite"
				>
					…
				</span>
			</div>

			<input
				ref="fileInputRef"
				type="file"
				class="sr-only"
				accept="image/jpeg,image/png,image/webp"
				:disabled="isAvatarBusy"
				@change="onFileSelected"
			/>

			<div class="flex flex-wrap items-center justify-center gap-4">
				<button
					type="button"
					class="text-caption font-medium text-primary underline-offset-2 hover:underline disabled:opacity-60"
					:disabled="isAvatarBusy || isAvatarLoading"
					@click="openFilePicker"
				>
					{{ pictureUrl ? 'Change photo' : 'Add photo' }}
				</button>
				<button
					v-if="pictureUrl"
					type="button"
					class="text-caption text-on-surface-muted underline-offset-2 hover:text-on-surface hover:underline disabled:opacity-60"
					:disabled="isAvatarBusy"
					@click="removeAvatar"
				>
					Remove
				</button>
			</div>

			<p
				v-if="avatarError"
				class="text-center text-caption text-critical"
				role="status"
			>
				{{ avatarError }}
			</p>
		</section>

		<!-- TODO: Implement real notifications settings -->
		<!-- <section class="flex flex-col gap-3">
			<Typography
				variant="title"
				as="h2"
				class="font-semibold text-on-surface"
			>
				Notifications
			</Typography>
			<div
				class="flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-surface-elevated px-4 py-3"
			>
				<Typography
					variant="body"
					as="span"
					class="font-medium text-on-surface"
				>
					Allow Notifications
				</Typography>
				<Switch
					v-model="allowNotifications"
					aria-label="Allow Notifications"
				/>
			</div>
		</section> -->

		<section class="flex flex-col gap-1">
			<Typography
				variant="title"
				as="h2"
				class="font-semibold text-on-surface"
			>
				Display
			</Typography>
			<Typography variant="body" colour="on-surface-muted" as="p">
				Customize how the app looks.
			</Typography>
		</section>

		<!-- Hero uses rem utilities so they scale with html font-size -->
		<section
			class="flex flex-col items-center text-center"
			aria-live="polite"
		>
			<p class="text-6xl font-semibold leading-none text-primary">Aa</p>
			<p class="mt-4 text-title font-semibold text-on-surface">Heading</p>
			<p class="mt-1 text-body text-on-surface-muted">
				Body text sample for reading comfort.
			</p>
			<p class="mt-1 text-caption text-on-surface-muted">
				Caption · secondary
			</p>
		</section>

		<div
			role="radiogroup"
			aria-label="Text size"
			class="flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-surface-elevated"
			:style="{
				// Keep chrome (padding, radio) at medium size regardless of hub scale.
				fontSize: `calc(16px / var(--app-font-scale, 1))`,
			}"
		>
			<button
				v-for="(option, index) in options"
				:key="option.value"
				type="button"
				role="radio"
				:aria-checked="textSize === option.value"
				:disabled="isSaving"
				class="flex w-full items-center gap-3 text-left transition-colors disabled:opacity-70"
				:class="[
					textSize === option.value
						? 'bg-primary/10'
						: 'bg-surface-elevated hover:bg-surface',
					index > 0 ? 'border-t border-border-subtle' : '',
				]"
				:style="{
					padding: '1em 1em',
					gap: '0.75em',
				}"
				@click="selectSize(option.value)"
			>
				<span
					class="flex shrink-0 items-center justify-center rounded-full border-2"
					:class="
						textSize === option.value
							? 'border-primary'
							: 'border-border'
					"
					:style="{ width: '1.25em', height: '1.25em' }"
					aria-hidden="true"
				>
					<span
						v-if="textSize === option.value"
						class="rounded-full bg-primary"
						:style="{ width: '0.625em', height: '0.625em' }"
					/>
				</span>

				<span
					class="flex-1 font-medium leading-snug text-on-surface"
					:style="{ fontSize: optionBodyFontSize(option.value) }"
					>{{ option.label }}</span
				>

				<span
					class="shrink-0 font-semibold leading-none text-on-surface-muted"
					aria-hidden="true"
					:style="{ fontSize: optionBodyFontSize(option.value) }"
					>Aa</span
				>
			</button>
		</div>

		<p v-if="syncError" class="text-caption text-critical" role="status">
			Saved on this device. Cloud sync failed: {{ syncError }}
		</p>

		<button
			type="button"
			class="mt-4 self-center text-caption text-on-surface-muted underline-offset-2 hover:text-on-surface hover:underline disabled:opacity-60"
			:disabled="isLoggingOut"
			@click="logout"
		>
			Sign out
		</button>
	</div>
</template>
